/**
 * Translation layer between the `property_setting` DB columns and the nested
 * in-memory strategy shape the UI uses.
 *
 * The fat `flip` / `brrrr` / `rental` JSONB blobs were split into smaller,
 * purpose-grouped columns (see migration
 * `20260606120000_property_setting_split_strategy_columns.sql`):
 *
 *   flip_hard_money · flip_selling_cost · flip_holding_cost
 *   brrrr_hard_money · brrrr_long_term · brrrr_holding_cost
 *   rental_long_term
 *
 * The legacy `flip` / `brrrr` / `rental` blob columns have been dropped; their
 * per-strategy `financing_enabled` / `lender` / `loan_view` fields now live in
 * the `loans` table.
 *
 * `flip_selling_cost` was itself widened from a bare numeric percent to JSON
 * carrying both the percent and the dollars it comes to (migration
 * `20260822000000_flip_selling_cost_json.sql`) — see {@link FlipSellingCost}.
 *
 * `composeStrategySettings` reassembles the new columns back into the nested
 * `FlipSettings` / `BrrrrSettings` / `RentalSettings` shape so every existing
 * consumer keeps working unchanged. `splitStrategySettings` does the inverse
 * for writes, computing `maturity_date` from `loan_date` + `term`.
 */
import { addMonths, format, parseISO } from "date-fns";

import { termInMonths } from "@/lib/calculators";
import type {
  BrrrrSettings,
  FlipSettings,
  LoanInputs,
  RentalSettings,
} from "@/types/pipeline";

interface HoldingCost {
  hold_months?: number;
  taxes_monthly?: number;
  utilities_monthly?: number;
  /** BRRRR only. */
  mcf_enabled?: boolean;
  mcf_delay?: number;
}

/**
 * `flip_selling_cost` as it is stored since migration
 * `20260822000000_flip_selling_cost_json.sql`: the percent of ARV that drives
 * the math, plus the dollar figure it works out to, so the entered amount is
 * readable straight off the row instead of being recomputed from ARV.
 */
export interface FlipSellingCost {
  flip_selling_cost_percent?: number | null;
  flip_selling_cost_value?: number | null;
}

/** The raw `property_setting` columns this module reads. */
export interface RawStrategyColumns {
  flip_hard_money?: LoanInputs | null;
  /**
   * JSON since 20260822000000. A bare numeric percent is still accepted on
   * read: that's what every row held before the migration, and what a client
   * running against an un-migrated database still sees.
   */
  flip_selling_cost?: FlipSellingCost | number | string | null;
  flip_holding_cost?: HoldingCost | null;
  brrrr_hard_money?: LoanInputs | null;
  brrrr_long_term?: LoanInputs | null;
  brrrr_holding_cost?: HoldingCost | null;
  rental_long_term?: LoanInputs | null;
}

export interface ComposedStrategySettings {
  flip: FlipSettings;
  brrrr: BrrrrSettings;
  rental: RentalSettings;
}

/** Drop keys whose value is `undefined` so we never persist `undefined`. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k as keyof T] = v as T[keyof T];
  }
  return out;
}

/**
 * Normalise a loan for persistence: stamp the canonical `term_months` (so the
 * blob is migrated forward on every save) and recompute `maturity_date` from
 * `loan_date` + that term. `unit` is the slot default used to interpret a
 * legacy `term` when `term_months` is absent (months for hard money, years for
 * long-term). Returns the loan with `maturity_date: null` when the date or term
 * is missing/invalid.
 */
export function withMaturity(
  loan: LoanInputs,
  unit: "months" | "years",
): LoanInputs {
  const months = termInMonths(loan, unit);
  let maturity_date: string | null = null;
  if (loan.loan_date && months > 0) {
    const parsed = parseISO(loan.loan_date);
    if (!Number.isNaN(parsed.getTime())) {
      maturity_date = format(addMonths(parsed, months), "yyyy-MM-dd");
    }
  }
  return { ...loan, term_months: months, maturity_date };
}

/**
 * Read `flip_selling_cost` in either shape it can arrive in: the JSON object
 * written since 20260822000000, or the bare percent that predates it. Absent
 * or unparseable parts are simply left off, so a half-filled row never
 * fabricates a zero the user didn't enter.
 * Pure for testing.
 */
export function readSellingCost(
  raw: RawStrategyColumns["flip_selling_cost"],
): Pick<FlipSettings, "sell_pct" | "sell_value"> {
  if (raw == null) return {};

  // Legacy numeric column (or a numeric that arrived as a string).
  if (typeof raw === "number" || typeof raw === "string") {
    const pct = Number(raw);
    return Number.isFinite(pct) ? { sell_pct: pct } : {};
  }

  const out: Pick<FlipSettings, "sell_pct" | "sell_value"> = {};
  const pct = Number(raw.flip_selling_cost_percent);
  if (raw.flip_selling_cost_percent != null && Number.isFinite(pct)) {
    out.sell_pct = pct;
  }
  const value = Number(raw.flip_selling_cost_value);
  if (raw.flip_selling_cost_value != null && Number.isFinite(value)) {
    out.sell_value = value;
  }
  return out;
}

/**
 * Inverse of {@link readSellingCost}: the JSON the column now stores. `null`
 * when the strategy carries neither figure, matching what the column held
 * before a selling cost was ever entered.
 * Pure for testing.
 */
export function writeSellingCost(
  flip: Pick<FlipSettings, "sell_pct" | "sell_value">,
): FlipSellingCost | null {
  if (flip.sell_pct == null && flip.sell_value == null) return null;
  return {
    flip_selling_cost_percent: flip.sell_pct ?? null,
    flip_selling_cost_value: flip.sell_value ?? null,
  };
}

/** Selling cost assumed when a flip carries neither figure. */
export const DEFAULT_SELL_PCT = 7;

/**
 * A flip's selling cost, in both units, for everything that consumes it.
 *
 * `flip_selling_cost_value` wins: it is the amount actually entered, and every
 * save writes it. The percent is the fallback — rows written before the column
 * carried a value, and the legacy bare-numeric column — applied to ARV. With
 * neither, {@link DEFAULT_SELL_PCT} stands.
 *
 * Both units come back so callers that need a percent (`computeFlip`, which
 * takes `sellPct`) stay in step with the dollars rather than each deriving
 * their own. Note the consequence of the value winning: re-estimating ARV moves
 * the PERCENT, not the dollar amount.
 * Pure for testing.
 */
export function resolveSellingCost(
  flip: Pick<FlipSettings, "sell_pct" | "sell_value"> | null | undefined,
  arv: number,
): { value: number; pct: number } {
  const arvSafe = Number(arv) || 0;

  const value = Number(flip?.sell_value);
  if (flip?.sell_value != null && Number.isFinite(value)) {
    return { value, pct: arvSafe > 0 ? (value / arvSafe) * 100 : 0 };
  }

  const stored = Number(flip?.sell_pct);
  const pct =
    flip?.sell_pct != null && Number.isFinite(stored) ? stored : DEFAULT_SELL_PCT;
  return { value: arvSafe * (pct / 100), pct };
}

/** Reassemble the new columns into the nested strategy shape the UI expects. */
export function composeStrategySettings(
  raw: RawStrategyColumns | null | undefined,
): ComposedStrategySettings {
  const r = raw ?? {};
  return {
    flip: {
      ...(r.flip_holding_cost ?? {}),
      ...(r.flip_hard_money ? { hard_money: r.flip_hard_money } : {}),
      ...readSellingCost(r.flip_selling_cost),
    },
    brrrr: {
      ...(r.brrrr_holding_cost ?? {}),
      ...(r.brrrr_hard_money ? { hard_money: r.brrrr_hard_money } : {}),
      ...(r.brrrr_long_term ? { long_term: r.brrrr_long_term } : {}),
    },
    rental: {
      ...(r.rental_long_term ? { long_term: r.rental_long_term } : {}),
    },
  };
}

/** A write patch carrying any subset of the nested strategy objects. */
export interface StrategyPatch {
  flip?: FlipSettings;
  brrrr?: BrrrrSettings;
  rental?: RentalSettings;
}

/**
 * Inverse of {@link composeStrategySettings}: split nested strategy objects
 * into the DB columns. Only strategies present on the patch are emitted, so
 * partial saves (e.g. the Financing editor sending only `brrrr`) touch only
 * their columns. `maturity_date` is recomputed from each loan's date + term.
 */
export function splitStrategySettings(
  patch: StrategyPatch,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  if (patch.flip) {
    const f = patch.flip;
    out.flip_hard_money = f.hard_money ? withMaturity(f.hard_money, "months") : null;
    out.flip_selling_cost = writeSellingCost(f);
    out.flip_holding_cost = stripUndefined({
      hold_months: f.hold_months,
      taxes_monthly: f.taxes_monthly,
      utilities_monthly: f.utilities_monthly,
    });
  }

  if (patch.brrrr) {
    const b = patch.brrrr;
    out.brrrr_hard_money = b.hard_money ? withMaturity(b.hard_money, "months") : null;
    out.brrrr_long_term = b.long_term ? withMaturity(b.long_term, "years") : null;
    out.brrrr_holding_cost = stripUndefined({
      hold_months: b.hold_months,
      mcf_enabled: b.mcf_enabled,
      mcf_delay: b.mcf_delay,
      taxes_monthly: b.taxes_monthly,
      utilities_monthly: b.utilities_monthly,
    });
  }

  if (patch.rental) {
    const r = patch.rental;
    out.rental_long_term = r.long_term ? withMaturity(r.long_term, "years") : null;
  }

  return out;
}
